import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import QRCode from "qrcode";

export interface DeliveryNoteLine {
  productName: string;
  sku?: string | null;
  quantity: number;
  notes?: string | null;
}

export interface DeliveryNoteData {
  documentNumber: string;
  issueDate: string; // ISO
  loadingDateTime?: string | null;
  unloadingDateTime?: string | null;

  company: {
    name: string;
    nif?: string | null;
    email?: string | null;
    phone?: string | null;
  };

  loading: {
    address?: string | null;
    city?: string | null;
    postalCode?: string | null;
    country?: string | null;
  };
  unloading: {
    address?: string | null;
    city?: string | null;
    postalCode?: string | null;
    country?: string | null;
  };

  carrier: {
    name?: string | null;
    vehiclePlate?: string | null;
    driverName?: string | null;
  };

  totalVolumes?: number | null;
  totalWeightKg?: number | null;

  atDocumentNumber?: string | null;
  atAtcud?: string | null;
  /** Conteúdo fiscal oficial do QR devolvido pelo TOConline. */
  atQrCode?: string | null;
  /** Encomenda de origem (para emitir a guia oficial no TOConline a partir da janela). */
  orderId?: string | null;
  /** ID da Guia de Transporte oficial já emitida no TOConline. */
  officialGuideId?: string | null;

  items: DeliveryNoteLine[];
  notes?: string | null;
}

const fmtDate = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-PT");
};

const fmtDateTime = (iso?: string | null) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" });
};

const joinAddr = (a: { address?: string | null; city?: string | null; postalCode?: string | null; country?: string | null }) =>
  [a.address, [a.postalCode, a.city].filter(Boolean).join(" "), a.country].filter(Boolean).join(", ") || "—";

/** Dados fiscais do emitente (vendedor). */
const EMITTER = {
  name: "Filipe Ferreira Lopes",
  nif: "PT517564262",
  address: "R. do Parque Industrial 17, 4750-734 Tamel (São Veríssimo), Barcelos, Portugal",
  email: "geral@hexatech.pt",
  phone: "+351 932 819 322",
};

export async function generateDeliveryNotePdf(data: DeliveryNoteData): Promise<jsPDF> {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;
  let y = margin;

  // Header compacto: título + nº à esquerda, QR à direita
  const qrSize = 92;
  let qrDataUrl: string | null = null;
  if (data.atQrCode) {
    qrDataUrl = await QRCode.toDataURL(data.atQrCode, { margin: 0, width: 512 });
    doc.addImage(qrDataUrl, "PNG", pageWidth - margin - qrSize, y - 8, qrSize, qrSize);
  }
  const metaWidth = pageWidth - margin * 2 - (qrDataUrl ? qrSize + 16 : 0);

  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("GUIA DE TRANSPORTE", margin, y);
  y += 14;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text(data.documentNumber, margin, y);
  y += 14;

  // Meta em linhas compactas (2 colunas dentro do espaço à esquerda do QR)
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(90);
  const metaCol2 = margin + metaWidth / 2;
  doc.text(`Emitida: ${fmtDate(data.issueDate)}`, margin, y);
  doc.text(`Carga: ${fmtDateTime(data.loadingDateTime)}`, metaCol2, y);
  y += 11;
  if (data.atDocumentNumber) doc.text(`Nº AT: ${data.atDocumentNumber}`, margin, y);
  doc.text(`Descarga: ${fmtDateTime(data.unloadingDateTime)}`, metaCol2, y);
  y += 11;
  if (data.atAtcud) {
    doc.text(`ATCUD: ${data.atAtcud}`, margin, y);
    y += 11;
  }
  doc.setTextColor(0);
  y = Math.max(y, qrDataUrl ? margin - 8 + qrSize + 6 : y) + 2;
  doc.setDrawColor(180);
  doc.line(margin, y, pageWidth - margin, y);
  y += 12;

  // Emitente: faixa de uma linha (dados fiscais do vendedor)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.text("EMITENTE", margin, y);
  doc.setFont("helvetica", "normal");
  const emitterText = `${EMITTER.name} · NIF: ${EMITTER.nif} · ${EMITTER.address} · ${EMITTER.email} · ${EMITTER.phone}`;
  const emitterLines = doc.splitTextToSize(emitterText, pageWidth - margin * 2 - 58);
  doc.text(emitterLines, margin + 58, y);
  y += 11 * emitterLines.length + 6;

  // Secções em 2 colunas: Cliente | Transporte, Carga | Descarga
  const colWidth = (pageWidth - margin * 2 - 16) / 2;

  const renderSection = (x: number, startY: number, title: string, lines: string[]) => {
    let yy = startY;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setFillColor(240, 240, 240);
    doc.rect(x, yy, colWidth, 14, "F");
    doc.text(title, x + 6, yy + 10);
    yy += 19;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    for (const l of lines) {
      const wrapped = doc.splitTextToSize(l, colWidth - 12);
      doc.text(wrapped, x + 6, yy);
      yy += 11 * wrapped.length;
    }
    return yy;
  };

  const clientLines = [
    data.company.name,
    data.company.nif ? `NIF: ${data.company.nif}` : "",
    [data.company.email, data.company.phone].filter(Boolean).join(" · "),
  ].filter(Boolean);
  const carrierLines = [
    data.carrier.name ? `Transportador: ${data.carrier.name}` : "Transportador: —",
    data.carrier.vehiclePlate ? `Matrícula: ${data.carrier.vehiclePlate}` : "Matrícula: —",
    data.carrier.driverName ? `Motorista: ${data.carrier.driverName}` : "Motorista: —",
  ];

  const yLeft = renderSection(margin, y, "CLIENTE", clientLines);
  const yRight = renderSection(margin + colWidth + 16, y, "TRANSPORTE", carrierLines);
  y = Math.max(yLeft, yRight) + 8;

  const yLeft2 = renderSection(margin, y, "LOCAL DE CARGA", [joinAddr(data.loading)]);
  const yRight2 = renderSection(margin + colWidth + 16, y, "LOCAL DE DESCARGA", [joinAddr(data.unloading)]);
  y = Math.max(yLeft2, yRight2) + 10;

  // Items table
  autoTable(doc, {
    startY: y,
    head: [["#", "Produto", "SKU", "Qtd"]],
    body: data.items.map((it, i) => [
      String(i + 1),
      it.productName + (it.notes ? `\n${it.notes}` : ""),
      it.sku || "—",
      String(it.quantity),
    ]),
    styles: { fontSize: 9, cellPadding: 5 },
    headStyles: { fillColor: [33, 37, 41], textColor: 255, fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 30, halign: "center" },
      2: { cellWidth: 90 },
      3: { cellWidth: 50, halign: "right" },
    },
    margin: { left: margin, right: margin },
  });

  // @ts-expect-error lastAutoTable added by plugin
  y = (doc.lastAutoTable?.finalY ?? y) + 16;

  // Totals (volumes + weight)
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setFillColor(245, 245, 245);
  doc.rect(margin, y, pageWidth - margin * 2, 26, "F");
  doc.setTextColor(60);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const totalItems = data.items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
  doc.text(`Linhas: ${data.items.length}`, margin + 10, y + 16);
  doc.text(`Quantidade total: ${totalItems}`, margin + 150, y + 16);
  doc.text(`Volumes: ${data.totalVolumes ?? 0}`, margin + 320, y + 16);
  doc.text(`Peso bruto: ${(data.totalWeightKg ?? 0).toFixed(2)} kg`, margin + 430, y + 16);
  doc.setTextColor(0);
  y += 38;

  // Notes
  if (data.notes) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("Observações", margin, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const wrapped = doc.splitTextToSize(data.notes, pageWidth - margin * 2);
    doc.text(wrapped, margin, y);
    y += 12 * wrapped.length + 8;
  }

  // Signatures
  const pageHeight = doc.internal.pageSize.getHeight();
  const signY = pageHeight - 100;
  doc.setDrawColor(150);
  doc.line(margin, signY, margin + 200, signY);
  doc.line(pageWidth - margin - 200, signY, pageWidth - margin, signY);
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.text("Assinatura do transportador", margin, signY + 12);
  doc.text("Assinatura do destinatário", pageWidth - margin - 200, signY + 12);

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(
    `Documento gerado em ${new Date().toLocaleString("pt-PT")} — ${data.documentNumber}`,
    pageWidth / 2,
    pageHeight - 30,
    { align: "center" }
  );

  // As Guias de Transporte são impressas em três vias.
  // Duplica todas as páginas para manter guias longas completas em cada via.
  const basePageCount = doc.getNumberOfPages();
  const internalPages = (doc.internal as unknown as { pages: string[][] }).pages;
  for (let copy = 1; copy < 3; copy += 1) {
    for (let sourcePage = 1; sourcePage <= basePageCount; sourcePage += 1) {
      const sourceContent = internalPages[sourcePage];
      doc.addPage();
      const targetPage = doc.getNumberOfPages();
      internalPages[targetPage] = sourceContent ? [...sourceContent] : [];
    }
  }

  const copyLabels = ["ORIGINAL", "DUPLICADO", "TRIPLICADO"];
  copyLabels.forEach((label, copyIndex) => {
    for (let pageInCopy = 1; pageInCopy <= basePageCount; pageInCopy += 1) {
      const pageNumber = copyIndex * basePageCount + pageInCopy;
      doc.setPage(pageNumber);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(80);
      doc.text(
        basePageCount > 1 ? `${label} · pág. ${pageInCopy}/${basePageCount}` : label,
        pageWidth - margin,
        22,
        { align: "right" },
      );
    }
  });

  return doc;
}
